"use client"

import { useCallback } from "react"

import { analytics } from "@/lib/analytics/client"
import { createClient } from "@/lib/supabase/client"

import { signupWithProfile } from "@/app/actions/signup"

import { useSignupContext } from "./context"
import type { Gender, PendingSignupData, SignupControllerReturn } from "./types"

const STORAGE_KEY = "dzp_pending_signup"

export function useSignupController(): SignupControllerReturn {
  const {
    email,
    firstName,
    lastName,
    birthDate,
    gender,
    setStep,
    setIsLoading,
    setIsGoogleLoading,
    setIsMagicLinkSent,
    setError,
  } = useSignupContext()

  const supabase = createClient()

  const handleGoogleSignup = useCallback(async () => {
    setIsGoogleLoading(true)
    setError(null)

    analytics.capture("signup_started", {
      method: "google_oauth",
    })

    const pendingCourse =
      typeof window !== "undefined"
        ? sessionStorage.getItem("dzp_pending_course")
        : null
    const nextUrl = pendingCourse
      ? `/cadastro/completar-perfil?curso=${pendingCourse}`
      : "/cadastro/completar-perfil"

    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/auth/callback?next=${encodeURIComponent(nextUrl)}`,
      },
    })

    if (error) {
      setError("Erro ao conectar com Google. Tente novamente.")
      setIsGoogleLoading(false)
    }
  }, [supabase, setIsGoogleLoading, setError])

  const handleEmailSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      setError(null)
      setStep("profile")
    },
    [setStep, setError]
  )

  const handleProfileSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      setIsLoading(true)
      setError(null)

      if (!firstName || !lastName || !birthDate || !gender) {
        setError("Por favor, preencha todos os campos.")
        setIsLoading(false)
        return
      }

      const pendingCourse =
        typeof window !== "undefined"
          ? sessionStorage.getItem("dzp_pending_course")
          : null

      // Use server action to signup with profile data in metadata
      analytics.capture("signup_profile_submitted", {
        has_pending_course: Boolean(pendingCourse),
      })

      try {
        const result = await signupWithProfile({
          email,
          firstName,
          lastName,
          birthDate,
          gender: gender as Gender,
          ...(pendingCourse && { courseSlug: pendingCourse }),
        })

        if (!result.success) {
          setError(result.error || "Erro ao criar conta. Tente novamente.")
        } else {
          setIsMagicLinkSent(true)
        }
      } catch {
        setError("Erro ao criar conta. Tente novamente.")
      } finally {
        setIsLoading(false)
      }
    },
    [
      email,
      firstName,
      lastName,
      birthDate,
      gender,
      setIsLoading,
      setError,
      setIsMagicLinkSent,
    ]
  )

  return {
    handleGoogleSignup,
    handleEmailSubmit,
    handleProfileSubmit,
  }
}

// Keep these for backwards compatibility and potential fallback
export function getPendingSignupData(): PendingSignupData | null {
  if (typeof window === "undefined") return null
  const data = localStorage.getItem(STORAGE_KEY)
  if (!data) return null
  try {
    return JSON.parse(data) as PendingSignupData
  } catch {
    return null
  }
}

export function clearPendingSignupData(): void {
  if (typeof window === "undefined") return
  localStorage.removeItem(STORAGE_KEY)
}
